import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, ListFilter, Search, CreditCard, Wallet } from "lucide-react";
import CommonGrid from "@/components/pos/admin/common/grid";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import AdvanceFiltersModal, { type FilterConfig } from "@/components/pos/admin/common/advance-filters-modal";
import { useDebounce } from "@/hooks/pos/use-debounce";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { NameAvatar } from "@/components/pos/admin/common/avatar";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import DetailModal from "@/components/pos/admin/common/detail-modal";
import { posApiFetch } from "@/utils/pos-api";
import type { Location, OrderWithTax } from "@/types/pos";
import { useToast } from "@/hooks/use-toast";

const fetchOrders = async (locationId?: string) => {
  const url = locationId ? `/v1/pos/orders?locationId=${locationId}` : `/v1/pos/orders`;
  const res = await posApiFetch(url);
  if (!res.ok) throw new Error("Failed to fetch orders");
  return res.json();
};

const fetchLocations = async () => {
  const res = await posApiFetch("/v1/pos/locations");
  if (!res.ok) throw new Error("Failed to fetch locations");
  return res.json();
};

type OrdersState = {
  location?: string;
  status?: string;
  currentPage?: number;
  itemsPerPage?: number;
  searchQuery?: string;
  dateRange?: { from: string; to: string };
  priceRange?: { min: string; max: string };
};

const defaultOrdersState: OrdersState = {
  location: "all",
  status: "all",
  currentPage: 1,
  itemsPerPage: 10,
  priceRange: { min: "", max: "" },
};

const normalizeDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    if (!value.trim()) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

export default function OrdersPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();
  const [detailModal, setDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderWithTax | null>(null);

  useEffect(() => { document.title = "Orders | SECH Admin"; }, []);

  const [ordersState, setOrdersState] = usePagePersistentState<OrdersState>("orders", defaultOrdersState);
  const dateRange = ordersState.dateRange;
  const searchQuery = ordersState.searchQuery;
  const currentPage = ordersState.currentPage;
  const itemsPerPage = ordersState.itemsPerPage;
  const statusFilter = ordersState.status;
  const selectedLocation = ordersState.location;
  const priceRange = ordersState.priceRange;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "orders_table",
    defaultColumn: "createdAt",
    defaultDirection: "desc",
  });

  const statusOptions = [
    { label: "All", value: "all" },
    { label: "Pending", value: "pending" },
    { label: "Preparing", value: "preparing" },
    { label: "Ready", value: "ready" },
    { label: "Delivered", value: "delivered" },
    { label: "Cancelled", value: "cancelled" },
  ];

  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    status: "", dateRange: { from: "", to: "" }, location: "", priceRange: { min: "", max: "" },
  });

  useEffect(() => {
    setFilterValues((prev) => ({
      ...prev,
      status: statusFilter ?? "",
      dateRange: { from: dateRange?.from ?? "", to: dateRange?.to ?? "" },
      location: selectedLocation ?? "",
      priceRange: { min: priceRange?.min ?? "", max: priceRange?.max ?? "" },
    }));
  }, [statusFilter, selectedLocation, dateRange, priceRange]);

  const handleApplyFilters = (values: Record<string, any>) => {
    const fromDate = normalizeDate(values.dateRange?.from);
    const toDate = normalizeDate(values.dateRange?.to);
    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      toast({ title: "Invalid filters", description: '"From" date cannot be after "To" date', variant: "destructive" });
      return;
    }
    setFilterValues(values);
    setOrdersState((prev) => ({
      ...prev,
      status: values.status,
      location: values.location ?? "all",
      currentPage: 1,
      dateRange: { from: values.dateRange?.from ?? "", to: values.dateRange?.to ?? "" },
      priceRange: { min: values.priceRange?.min ? String(values.priceRange.min) : "", max: values.priceRange?.max ? String(values.priceRange.max) : "" },
    }));
  };

  const { data: orders = [], isLoading, error } = useQuery({
    queryKey: ["/v1/pos/orders", selectedLocation],
    queryFn: () => fetchOrders(selectedLocation === "all" ? undefined : selectedLocation),
    retry: 3,
    retryDelay: 1000,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ["/v1/pos/locations"],
    queryFn: fetchLocations,
    retry: 3,
    retryDelay: 1000,
  });

  const getFilterCount = () => {
    let count = 0;
    if (ordersState.status && ordersState.status !== "all") count += 1;
    const fromDate = normalizeDate(ordersState.dateRange?.from);
    const toDate = normalizeDate(ordersState.dateRange?.to);
    if (fromDate || toDate) count += 1;
    const minStr = ordersState.priceRange?.min?.toString()?.trim() ?? "";
    const maxStr = ordersState.priceRange?.max?.toString()?.trim() ?? "";
    if (minStr !== "" || maxStr !== "") count += 1;
    return count;
  };

  const orderFilters: FilterConfig[] = useMemo(() => [
    { key: "status", label: "Status", type: "select", options: statusOptions, placeholder: "Select Option" },
    { key: "dateRange", label: "Date Range", type: "dateRange" },
    { key: "priceRange", label: "Price Range (USD)", type: "numberRange", min: 0, max: 100, step: 1, prefix: "$" },
  ], []);

  const handleClear = () => {
    setFilterValues({ status: "all", dateRange: { from: "", to: "" }, location: "all", priceRange: { min: "", max: "" } });
    setOrdersState((prev) => ({ ...prev, status: "all", location: "all", dateRange: { from: "", to: "" }, currentPage: 1, priceRange: { min: "", max: "" } }));
  };

  const filteredLocations = useMemo(() => {
    const activeLocations = locations.filter((loc: any) => loc.active);
    const orderLocations = orders.filter((o: any) => o.location?.id && o.location?.name).map((o: any) => o.location);
    const locationMap = new Map<number, any>();
    [...activeLocations, ...orderLocations].forEach((loc: any) => { if (loc?.id) locationMap.set(loc.id, loc); });
    return Array.from(locationMap.values());
  }, [locations, orders]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "preparing": return "bg-blue-100 text-blue-800 border-blue-200";
      case "ready": return "bg-green-100 text-green-800 border-green-200";
      case "delivered": return "bg-gray-100 text-gray-800 border-gray-200";
      case "cancelled": return "bg-red-100 text-red-800 border-red-200";
      case "completed": return "bg-green-100 text-green-800 border-green-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusDotColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-800";
      case "preparing": return "bg-blue-800";
      case "ready": return "bg-green-800";
      case "delivered": return "bg-gray-800";
      case "cancelled": return "bg-red-800";
      case "completed": return "bg-green-800";
      default: return "bg-gray-800";
    }
  };

  const handleViewOrderDetails = (row: OrderWithTax) => { setSelectedOrder(row); setDetailModal(true); };

  const setTableColumns = useMemo(() => [
    { label: "Order Number", field: "orderNumber", sortable: true },
    {
      label: "Items", field: "items",
      cellTemplate: (items: any[]) => {
        if (!items || items.length === 0) return <span className="text-[13px] text-[var(--secondary-text-color)]">—</span>;
        const [first, ...rest] = items;
        const getItemLabel = (item: any) => { const name = item?.product?.name || item?.customDrink?.name || "Item"; return `${name} × ${item.quantity ?? 1}`; };
        return (
          <div className="flex items-center gap-2">
            <span className="text-[13px] text-[#505258]">{getItemLabel(first)}</span>
            {rest.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="h-[26px] w-[26px] rounded-full bg-[var(--secondary-background-color)] text-[12px] leading-[14px] text-[#53585B] font-medium flex items-center justify-center hover:bg-[var(--primary-background-hover-color)]">{rest.length}+</button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[180px] p-0 rounded-[6px]">
                  {rest.map((item: any) => (
                    <DropdownMenuItem key={item.id} className="flex items-center gap-2 border-b border-[#E4E5EB] text-[13px]">
                      <span className="text-[12px] leading-[14px] font-medium text-[#53585B]">{getItemLabel(item)}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        );
      },
    },
    { label: "Date & Time", field: "createdAt", sortable: true, cellTemplate: (value: string) => formatDate(value) },
    {
      label: "Pickup Person", field: "user",
      cellTemplate: (value: any, row: any) => (
        <div className="flex items-center gap-2">
          {value && <NameAvatar name={value?.pickupPerson || "-"} imageUrl={row?.imageUrl} />}
          <span className="text-[13px] text-[#505258]">{value?.pickupPerson || "-"}</span>
        </div>
      ),
    },
    {
      label: "Status", field: "status", sortable: true,
      cellTemplate: (value: string) => (
        <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${getStatusColor(value)}`}>
          <span className={`h-2 w-2 rounded-full mr-1 ${getStatusDotColor(value)} inline-flex items-center justify-center`} />
          <span className="leading-5 text-[13px] font-normal">{value.charAt(0).toUpperCase() + value.slice(1)}</span>
        </span>
      ),
    },
    {
      label: "Location", field: "location", hidden: selectedLocation !== "all",
      cellTemplate: (value: Location) => (
        <span className="text-[13px] flex items-center gap-2">
          <span className="h-[18px] w-[18px] rounded-[4px]" style={{ backgroundColor: value?.colorCode }} />
          <span className="text-[var(--secondary-text-color)] text-[13px] leading-5 font-normal mt-1 rounded-xl">{value?.name || "—"}</span>
        </span>
      ),
    },
    { label: "Location Details", field: "locationDetails", cellTemplate: (value: string) => <span className="text-[13px] text-[#505258] text-center">{value || "-"}</span> },
    {
      label: "Method", field: "paymentMethod",
      cellTemplate: (value: string, row: any) => {
        const isCard = value === 'CARD' || (!value && !row.transactionHash);
        return isCard ? (
          <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">
            <CreditCard className="h-2.5 w-2.5" />Card
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-700">
            <Wallet className="h-2.5 w-2.5" />Token
          </span>
        );
      },
    },
    {
      label: "Total", field: "totalUsd", sortable: true,
      cellTemplate: (value: string, row: any) => {
        const isCard = row.paymentMethod === 'CARD' || (!row.paymentMethod && !row.transactionHash);
        return isCard
          ? <span className="text-[13px] text-[var(--secondary-text-color)]">${value} USD</span>
          : <span className="text-[13px] text-[var(--secondary-text-color)]">{parseFloat(row?.totalSech ?? "0").toFixed(2)} SECH</span>;
      },
    },
    {
      label: "action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: string, row: any) => (
        <span className="group text-[13px] hover:text-[#CC1417] text-[var(--primary-color)] flex items-center gap-2 cursor-pointer" onClick={() => handleViewOrderDetails(row)}>
          Details
          <ArrowRight className="transform transition-transform duration-300 group-hover:translate-x-1" width={16} height={16} />
        </span>
      ),
    },
  ], [selectedLocation]);

  const filteredOrders = useMemo(() =>
    orders.filter((order: any) => {
      const search = debouncedSearch?.trim().toLowerCase() ?? "";
      const matchesStatus = statusFilter === "all" || order.status === statusFilter;
      const searchItem = order?.items?.some((item: any) => item?.product?.name?.toLowerCase().includes(search) || item?.customDrink?.name?.toLowerCase().includes(search));
      const matchesSearch = !search || order.orderNumber.toLowerCase().includes(search) || order.status.toLowerCase().includes(search) || order?.location?.name?.toLowerCase().includes(search) || order.locationDetails?.toLowerCase().includes(search) || order.user?.pickupPerson?.toLowerCase().includes(search) || order.totalUsd.toString().includes(search) || searchItem;
      const fromDate = normalizeDate(ordersState.dateRange?.from);
      const toDate = normalizeDate(ordersState.dateRange?.to);
      const createdAt = new Date(order.createdAt).getTime();
      const matchesDateRange = fromDate && toDate ? createdAt >= fromDate.getTime() && createdAt <= toDate.getTime() : true;
      const minStr = ordersState.priceRange?.min ?? "";
      const maxStr = ordersState.priceRange?.max ?? "";
      const min = minStr === "" ? null : Number(minStr);
      const max = maxStr === "" ? null : Number(maxStr);
      const total = Number(order.totalUsd);
      const matchesPriceRange = (min === null || total >= min) && (max === null || total <= max);
      return matchesStatus && matchesSearch && matchesDateRange && matchesPriceRange;
    }), [orders, statusFilter, debouncedSearch, ordersState.dateRange, ordersState.priceRange]
  );

  const sortedOrders = useMemo(() => {
    return [...filteredOrders].sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "orderNumber": aVal = a?.orderNumber ?? ""; bVal = b?.orderNumber ?? ""; break;
        case "createdAt": aVal = a?.createdAt ? new Date(a.createdAt).getTime() : 0; bVal = b?.createdAt ? new Date(b.createdAt).getTime() : 0; break;
        case "status": aVal = a?.status ?? ""; bVal = b?.status ?? ""; break;
        case "totalUsd": aVal = parseFloat(a?.totalUsd ?? "0"); bVal = parseFloat(b?.totalUsd ?? "0"); break;
        default: aVal = a?.createdAt ? new Date(a.createdAt).getTime() : 0; bVal = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredOrders, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedOrders.length / (itemsPerPage ?? 10));
  const startIndex = ((currentPage ?? 1) - 1) * (itemsPerPage ?? 10);
  const paginatedOrders = sortedOrders.slice(startIndex, startIndex + (itemsPerPage ?? 10));

  const handlePageChange = (page: number) => { setOrdersState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setOrdersState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems: sortedOrders.length, handlePageChange, handleItemsPerPageChange };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md"><CardContent className="p-6">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Orders</h2>
          <p className="text-gray-600 mb-4">Failed to load order history</p>
          <Button onClick={() => window.location.reload()} className="w-full">Reload Page</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">Orders</p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className={`flex h-8 items-center px-1 p-2 rounded-sm ${getFilterCount() > 0 ? "bg-[var(--secondary-background-color)]" : ""}`}>
            <Button onClick={() => setIsFilterOpen(true)} variant="ghost" className={`px-2 text-[var(--primary-text-color)] flex items-center text-[13px] font-medium leading-[15px] justify-center gap-2 rounded-[4px] ${getFilterCount() > 0 ? "h-6 hover:bg-[var(--primary-background-hover-color)]" : "h-8"}`}>
              <ListFilter className="h-4 w-4" /> Filter
              {getFilterCount() > 0 && <span className="text-[13px] bg-[var(--primary-color)] h-4 w-4 flex items-center justify-center rounded-full leading-[15px] font-medium text-white">{getFilterCount()}</span>}
            </Button>
            {getFilterCount() > 0 && (
              <>
                <span className="text-[13px] text-[var(--secondary-text-color)] px-1">|</span>
                <Button type="button" onClick={handleClear} className="text-[13px] text-[var(--primary-text-color)] font-medium px-2 h-6 hover:bg-[var(--primary-background-hover-color)] bg-transparent">Clear</Button>
              </>
            )}
          </div>
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center" type="text" placeholder="Search" value={searchQuery ?? ""} onChange={(e) => setOrdersState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))} />
          </div>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="main">
          <div className="tabs flex items-center gap-[8px] border-b border-[var(--layout-border-color)] pb-[5px]">
            <div onClick={() => setOrdersState((prev) => ({ ...prev, location: "all", currentPage: 1 }))} className={`tab cursor-pointer h-[32px] px-[8px] py-[4px] rounded-[4px] flex items-center text-[13px] leading-[16px] font-normal relative ${selectedLocation === "all" ? "bg-[var(--primary-background-hover-color)]" : ""}`}>
              <span className="text text-[var(--secondary-text-color)]">All</span>
              <div className={`absolute bottom-[-6px] left-0 right-0 h-[2px] bg-[var(--primary-color)] rounded-t-full ${selectedLocation === "all" ? "block" : "hidden"}`} />
            </div>
            {filteredLocations?.map((loc: any) => (
              <div key={loc?.id} onClick={() => setOrdersState((prev) => ({ ...prev, location: loc?.id?.toString() ?? "", currentPage: 1 }))} className={`tab cursor-pointer h-[32px] pr-[4px] pl-[8px] py-[4px] rounded-[4px] flex items-center text-[13px] leading-[16px] font-normal relative ${selectedLocation === loc?.id?.toString() ? "bg-[var(--primary-background-hover-color)]" : ""}`}>
                <span className="text mr-[8px] text-[var(--secondary-text-color)]">{loc?.name?.charAt(0).toUpperCase() + loc?.name?.slice(1)}</span>
                <span className="h-4 w-4 rounded-[4px]" style={{ backgroundColor: loc?.colorCode }} />
                <div className={`absolute bottom-[-6px] left-0 right-0 h-[2px] bg-[var(--primary-color)] rounded-t-full ${selectedLocation === loc?.id?.toString() ? "block" : "hidden"}`} />
              </div>
            ))}
          </div>
        </div>
        <div className="pt-3 h-[calc(100vh-170px)]">
          {!paginatedOrders.length && !isLoading ? (
            <NoRecordFound title="Orders" description="There are no records available." />
          ) : (
            <CommonGrid
              rowData={paginatedOrders}
              isLoading={isLoading}
              columns={setTableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>
      <AdvanceFiltersModal isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filter" filters={orderFilters} initialValues={filterValues} onApply={handleApplyFilters} onClearAll={handleClear} />
      {detailModal && <DetailModal isOpen={detailModal} onClose={() => { setDetailModal(false); setSelectedOrder(null); }} title="Order Details" data={selectedOrder} />}
    </div>
  );
}
